<template>
  <div class="admin-salesperson-page container py-4">
    <div class="skip-links">
      <a href="#main-content" class="skip-link">Zum Hauptinhalt</a>
    </div>

    <div class="container-fluid p-4" id="main-content">
      <!-- Breadcrumb Navigation -->
      <nav aria-label="breadcrumb" class="mb-4">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <router-link to="/admin">
              <font-awesome-icon icon="cog" class="me-1" />
              Admin
            </router-link>
          </li>
          <li class="breadcrumb-item active" aria-current="page">
            <font-awesome-icon icon="building" class="me-1" />
            Verkäufer-Daten
          </li>
        </ol>
      </nav>

      <!-- Page Header -->
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h1 class="mb-0">
          <font-awesome-icon icon="building" class="me-2" />
          Verkäufer-Informationen
        </h1>
        <BackButton fallback="admin" />
      </div>

      <!-- Salesperson Settings -->
      <div class="card shadow-sm mb-4">
        <div class="card-body">
          <p class="text-muted mb-4">
            Diese Daten werden auf allen generierten Rechnungen angezeigt und identifizieren dich als Verkäufer.
          </p>

          <form @submit.prevent="saveSalesPersonData">
            <div class="row g-3">
              <div class="col-12">
                <label class="form-label">Firmenname *</label>
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="salesPersonData.company"
                  required
                  placeholder="Ihre Firma GmbH"
                >
              </div>
              
              <div class="col-md-8">
                <label class="form-label">Straße *</label>
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="salesPersonData.street"
                  required
                  placeholder="Musterstraße"
                >
              </div>
              
              <div class="col-md-4">
                <label class="form-label">Hausnummer *</label>
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="salesPersonData.number"
                  required
                  placeholder="123"
                >
              </div>
              
              <div class="col-md-4">
                <label class="form-label">PLZ *</label>
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="salesPersonData.zip"
                  required
                  placeholder="12345"
                >
              </div>
              
              <div class="col-md-8">
                <label class="form-label">Stadt *</label>
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="salesPersonData.city"
                  required
                  placeholder="Musterstadt"
                >
              </div>
              
              <div class="col-12">
                <label class="form-label">Land *</label>
                <select class="form-select" v-model="salesPersonData.country" required>
                  <option value="">Land auswählen</option>
                  <option value="Deutschland">Deutschland</option>
                </select>
              </div>
              
              <div class="col-12">
                <label class="form-label">E-Mail-Adresse *</label>
                <input 
                  type="email" 
                  class="form-control" 
                  v-model="salesPersonData.email"
                  required
                  placeholder="kontakt@firma.de"
                >
              </div>
              
              <div class="col-md-6">
                <label class="form-label">USt-IdNr. (optional)</label>
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="salesPersonData.USt_Id"
                  placeholder="DE123456789"
                >
                <div class="form-text">Umsatzsteuer-Identifikationsnummer</div>
              </div>
              
              <div class="col-md-6">
                <label class="form-label">Handelsregistereintrag (optional)</label>
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="salesPersonData.commercialRegister"
                  placeholder="HRB 12345"
                >
                <div class="form-text">Handelsregisternummer</div>
              </div>
              
              <div class="col-md-6">
                <label class="form-label">Geschäftsführung (optional)</label>
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="salesPersonData.ceo"
                  placeholder="Christian Jesse"
                >
                <div class="form-text">Name der Geschäftsführung</div>
              </div>
              
              <div class="col-md-6">
                <label class="form-label">Telefon (optional)</label>
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="salesPersonData.phone"
                  placeholder="+49 3379 - 322 55 00"
                >
                <div class="form-text">Telefonnummer</div>
              </div>
              
              <div class="col-md-6">
                <label class="form-label">Fax (optional)</label>
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="salesPersonData.fax"
                  placeholder="+49 3379 - 322 55 29"
                >
                <div class="form-text">Faxnummer</div>
              </div>
              
              <div class="col-md-6">
                <label class="form-label">Website (optional)</label>
                <input 
                  type="url" 
                  class="form-control" 
                  v-model="salesPersonData.url"
                  placeholder="www.pronomix.de"
                >
                <div class="form-text">Website-URL</div>
              </div>

              <div class="row g-3 mt-3">
                <div class="col-12">
                  <h6 class="text-primary mb-3">Bankinformationen</h6>
                </div>
                
                <div class="col-md-6">
                  <label class="form-label">IBAN *</label>
                  <div class="input-group">
                    <input 
                      type="text" 
                      class="form-control"
                      :class="{
                        'is-valid': ibanValidation.valid,
                        'is-invalid': ibanValidation.error && salesPersonData.iban.length > 0
                      }"
                      v-model="salesPersonData.iban"
                      required
                      placeholder="DE43 1001 0010 0060 3231 08"
                      @input="resetIbanValidation"
                    />
                    <button 
                      type="button" 
                      class="btn btn-outline-secondary" 
                      @click="validateIban(salesPersonData.iban)"
                      :disabled="!salesPersonData.iban || ibanValidation.loading"
                    >
                      <font-awesome-icon 
                        v-if="ibanValidation.loading" 
                        icon="spinner" 
                        class="fa-spin me-1" 
                      />
                      <font-awesome-icon 
                        v-else 
                        icon="check" 
                        class="me-1" 
                      />
                      {{ ibanValidation.loading ? 'Prüfe...' : 'Prüfen' }}
                    </button>
                  </div>
                  <div class="form-text">International Bank Account Number</div>
                  
                  <!-- IBAN Validation Feedback -->
                  <div v-if="ibanValidation.valid && ibanValidation.bankName" class="form-text text-success">
                    <font-awesome-icon icon="bank" class="me-1" />
                    <strong>{{ ibanValidation.bankName }}</strong>
                  </div>
                  <div v-if="ibanValidation.error" class="form-text text-danger">
                    <font-awesome-icon icon="exclamation-triangle" class="me-1" />
                    {{ ibanValidation.error }}
                  </div>
                </div>
                
                <div class="col-md-6">
                  <label class="form-label">BIC/SWIFT *</label>
                  <input 
                    type="text" 
                    class="form-control" 
                    v-model="salesPersonData.bic"
                    required
                    placeholder="PBNKDEFFXXX"
                  >
                  <div class="form-text">Bank Identifier Code</div>
                  <!-- BIC Auto-fill Feedback -->
                  <div v-if="ibanValidation.bicAutoFilled" class="form-text text-success">
                    <font-awesome-icon icon="check" class="me-1" />
                    BIC automatisch ausgefüllt
                  </div>
                </div>
                
                <div class="col-md-6">
                  <label class="form-label">Bankname (optional)</label>
                  <input 
                    type="text" 
                    class="form-control" 
                    v-model="salesPersonData.bankName"
                    placeholder="Postbank"
                  >
                  <div class="form-text">Name der Bank</div>
                  <!-- Bankname Auto-fill Feedback -->
                  <div v-if="ibanValidation.bankNameAutoFilled" class="form-text text-success">
                    <font-awesome-icon icon="check" class="me-1" />
                    Bankname automatisch ausgefüllt
                  </div>
                </div>
                
                <div class="col-md-6">
                  <label class="form-label">Kontonummer (optional)</label>
                  <input 
                    type="text" 
                    class="form-control" 
                    v-model="salesPersonData.accountNumber"
                    placeholder="190 114 002"
                  >
                  <div class="form-text">Kontonummer</div>
                </div>
                
                <div class="col-md-6">
                  <label class="form-label">BLZ (optional)</label>
                  <input 
                    type="text" 
                    class="form-control" 
                    v-model="salesPersonData.bankCode"
                    placeholder="100 500 00"
                  >
                  <div class="form-text">Bankleitzahl</div>
                </div>
              </div>
            </div>

            <div class="mt-4">
              <button 
                type="submit" 
                class="btn btn-primary"
                :disabled="saving"
              >
                <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
                <font-awesome-icon v-else icon="save" class="me-2" />
                {{ saving ? 'Speichern...' : 'Speichern' }}
              </button>
              
              <button 
                type="button" 
                class="btn btn-outline-secondary ms-2"
                @click="resetForm"
                :disabled="saving"
              >
                <font-awesome-icon icon="undo" class="me-2" />
                Zurücksetzen
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
 </template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import { showError, showSuccess, showConfirmDialogWithDeny } from '@/services/notification'
import { getSalesPersonData as getSalesPersonDataApi, saveSalesPersonData as saveSalesPersonDataApi } from '@/api/admin'
import { ibanApi } from '@/api/iban'


const saving = ref(false)
const loading = ref(false)
const originalData = ref({})
const hasUnsavedChanges = ref(false)
const dataLoaded = ref(false)

const salesPersonData = ref({
  company: '',
  street: '',
  number: '',
  zip: '',
  city: '',
  country: 'Deutschland',
  email: '',
  USt_Id: '',
  commercialRegister: '',
  ceo: '',
  phone: '',
  fax: '',
  url: '',
  accountNumber: '',
  bankCode: '',
  iban: '',
  bic: '',
  bankName: ''
})

const ibanValidation = ref({
  valid: false,
  loading: false,
  bankName: null,
  bic: null,
  error: null,
  bicAutoFilled: false,
  bankNameAutoFilled: false
})

const checkForChanges = () => {
  if (!dataLoaded.value || !originalData.value || Object.keys(originalData.value).length === 0) return false
  
  // Normalisiere die Daten für den Vergleich (entferne undefined/null Werte)
  const normalizeData = (data) => {
    const normalized = {}
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined && data[key] !== '') {
        normalized[key] = data[key]
      }
    })
    return normalized
  }
  
  const currentData = normalizeData(salesPersonData.value)
  const originalDataNormalized = normalizeData(originalData.value)
  
  return JSON.stringify(currentData) !== JSON.stringify(originalDataNormalized)
}

const confirmBeforeLeaving = async () => {
  if (!hasUnsavedChanges.value) {
    return true
  }
  
  const result = await showConfirmDialogWithDeny(
    'Ungespeicherte Änderungen',
    'Du hast ungespeicherte Änderungen. Möchtest du diese speichern, bevor du fortfährst?',
    'Speichern',
    'Verwerfen',
    'Abbrechen',
    {
      icon: 'warning',
      confirmButtonColor: '#0d6efd',
      denyButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d'
    }
  )
  
  if (result.isConfirmed) {
    // Speichere die Änderungen vor dem Verlassen
    await saveSalesPersonData()
    return true
  } else if (result.isDenied) {
    // Verwerfe die Änderungen
    hasUnsavedChanges.value = false
    return true
  } else {
    // Benutzer hat abgebrochen
    return false
  }
}

const loadSalesPersonData = async () => {
  try {
    loading.value = true
    const data = await getSalesPersonDataApi()
    if (data) {
      Object.assign(salesPersonData.value, data)
      originalData.value = { ...data }
    } else {
      // Wenn keine Daten vorhanden sind, setze originalData mit den Standardwerten
      originalData.value = { ...salesPersonData.value }
    }
  } catch (error) {
    if (error.response?.status !== 404) {
      console.error('Error loading sales person data:', error)
      showError('Fehler beim Laden der Verkäufer-Daten')
    } else {
      // Bei 404 (keine Daten vorhanden) setze originalData mit Standardwerten
      originalData.value = { ...salesPersonData.value }
    }
  } finally {
    loading.value = false
    // Setze dataLoaded erst nach dem Setzen aller Daten
    setTimeout(() => {
      dataLoaded.value = true
    }, 100)
  }
}

const saveSalesPersonData = async () => {
  try {
    saving.value = true
    
    const result = await saveSalesPersonDataApi(salesPersonData.value)
    
    originalData.value = { ...salesPersonData.value }
    hasUnsavedChanges.value = false
    showSuccess('Verkäufer-Daten erfolgreich gespeichert')
    
  } catch (error) {
    console.error('Error saving sales person data:', error)
    showError(error.response?.data?.message || 'Fehler beim Speichern der Verkäufer-Daten')
  } finally {
    saving.value = false
  }
}

const resetForm = () => {
  Object.assign(salesPersonData.value, originalData.value)
  hasUnsavedChanges.value = false
}

const resetIbanValidation = () => {
  // Temporär den Watcher deaktivieren
  const wasWatching = hasUnsavedChanges.value
  
  ibanValidation.value = {
    valid: false,
    loading: false,
    bankName: null,
    bic: null,
    error: null,
    bicAutoFilled: false,
    bankNameAutoFilled: false
  }
  
  // Nach dem Reset originalData aktualisieren, falls sich etwas geändert hat
  if (dataLoaded.value) {
    originalData.value = { ...salesPersonData.value }
    hasUnsavedChanges.value = false
  }
}

const validateIban = async (iban) => {
  if (!iban || iban.length < 10) {
    ibanValidation.value = {
      valid: false,
      loading: false,
      bankName: null,
      bic: null,
      error: null,
      bicAutoFilled: false,
      bankNameAutoFilled: false
    }
    return
  }

  ibanValidation.value.loading = true
  ibanValidation.value.error = null

  try {
    const result = await ibanApi.validateIban(iban)
    
    if (result.valid) {
      
      const wasBicEmpty = !salesPersonData.value.bic
      const bicAutoFilled = result.bic && wasBicEmpty
      
      
      const wasBankNameEmpty = !salesPersonData.value.bankName
      const bankNameAutoFilled = result.bankName && wasBankNameEmpty
      
      ibanValidation.value = {
        valid: true,
        loading: false,
        bankName: result.bankName,
        bic: result.bic,
        error: null,
        bicAutoFilled,
        bankNameAutoFilled
      }
      
      // Temporär den Watcher deaktivieren, um automatisches Ausfüllen nicht als Änderung zu werten
      const wasWatching = hasUnsavedChanges.value
      
      if (result.bic && wasBicEmpty) {
        salesPersonData.value.bic = result.bic
      }
      if (result.bankName && wasBankNameEmpty) {
        salesPersonData.value.bankName = result.bankName
      }
      
      // Nach dem automatischen Ausfüllen originalData aktualisieren
      if (bicAutoFilled || bankNameAutoFilled) {
        originalData.value = { ...salesPersonData.value }
        hasUnsavedChanges.value = false
      }
    } else {
      ibanValidation.value = {
        valid: false,
        loading: false,
        bankName: null,
        bic: null,
        error: result.error || 'Ungültige IBAN',
        bicAutoFilled: false,
        bankNameAutoFilled: false
      }
    }
  } catch (error) {
    console.error('IBAN-Validierungsfehler:', error)
    ibanValidation.value = {
      valid: false,
      loading: false,
      bankName: null,
      bic: null,
      error: 'Fehler bei der IBAN-Validierung',
      bicAutoFilled: false,
      bankNameAutoFilled: false
    }
  }
}

// Watcher für Änderungen
watch(salesPersonData, () => {
  hasUnsavedChanges.value = checkForChanges()
}, { deep: true })

// Router-Guard für Navigation
onBeforeRouteLeave(async (to, from, next) => {
  if (!hasUnsavedChanges.value) {
    next()
    return
  }
  
  const result = await showConfirmDialogWithDeny(
    'Ungespeicherte Änderungen',
    'Du hast ungespeicherte Änderungen. Möchtest du diese speichern, bevor du fortfährst?',
    'Speichern',
    'Verwerfen',
    'Abbrechen',
    {
      icon: 'warning',
      confirmButtonColor: '#0d6efd',
      denyButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d'
    }
  )
  
  if (result.isConfirmed) {
    // Speichere die Änderungen vor dem Verlassen
    try {
      await saveSalesPersonData()
      next()
    } catch (error) {
      console.error('Fehler beim Speichern:', error)
      next(false) // Verhindere Navigation bei Fehler
    }
  } else if (result.isDenied) {
    // Verwerfe die Änderungen und erlaube Navigation
    hasUnsavedChanges.value = false
    next()
  } else {
    // Benutzer hat abgebrochen - verhindere Navigation
    next(false)
  }
})

// Browser beforeunload Handler
const handleBeforeUnload = (e) => {
  if (hasUnsavedChanges.value) {
    e.preventDefault()
    e.returnValue = ''
  }
}

onMounted(() => {
  loadSalesPersonData()
  window.addEventListener('beforeunload', handleBeforeUnload)
})

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload)
})
</script>
